/**
 * Superadmin per-user spend and usage.
 * Regular /usage stays current-user-only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminUsageApi } from '../../services/cloudApi';
import { formatErrorMessage } from '../../utils/errorUtils';
import './Admin.css';
import '../usage/Usage.css';

const PERIOD_OPTIONS = [
    { value: 'daily', label: 'Daily (30 days)' },
    { value: 'monthly', label: 'Monthly (12 months)' },
];

const PROVIDER_LABELS = {
    bedrock: 'AWS Bedrock',
    anthropic: 'Anthropic',
    openai: 'OpenAI',
    gemini: 'Google Gemini',
    'gemini-vertex': 'Google Vertex AI',
    ollama: 'Ollama',
};

function formatNumber(n) {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
    return (n || 0).toLocaleString();
}

function formatCredits(credits) {
    const n = Number(credits) || 0;
    if (n === 0) return '0 cr';
    if (n < 0.01) return '<0.01 cr';
    if (n < 10) return n.toFixed(2) + ' cr';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' cr';
}

function formatUsd(usd) {
    const n = Number(usd) || 0;
    if (n === 0) return '$0.00';
    if (n < 0.01) return '<$0.01';
    return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatModelName(modelId) {
    if (!modelId) return 'Unknown';
    return modelId
        .replace('eu.anthropic.', '')
        .replace('global.anthropic.', '')
        .replace(/-v\d+:\d+$/, '');
}

export function AdminUsage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedUserId = searchParams.get('user') || '';

    const [period, setPeriod] = useState('monthly');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [leaderboard, setLeaderboard] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);

    const [analytics, setAnalytics] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [providerFilter, setProviderFilter] = useState('');

    const loadLeaderboard = useCallback(async () => {
        setListLoading(true);
        setListError(null);
        try {
            const data = await adminUsageApi.getLeaderboard({
                search: search || undefined,
                period,
                page,
                pageSize: 20,
            });
            setLeaderboard(data.users || []);
            setTotal(data.total || 0);
        } catch (err) {
            setListError(err.message);
        } finally {
            setListLoading(false);
        }
    }, [search, period, page]);

    useEffect(() => {
        loadLeaderboard();
    }, [loadLeaderboard]);

    const loadDetail = useCallback(async () => {
        if (!selectedUserId) {
            setAnalytics(null);
            setDetailError(null);
            return;
        }
        setDetailLoading(true);
        setDetailError(null);
        try {
            const data = await adminUsageApi.getUserAnalytics(selectedUserId, {
                period,
                provider: providerFilter || undefined,
            });
            setAnalytics(data);
        } catch (err) {
            setAnalytics(null);
            setDetailError(err.message);
        } finally {
            setDetailLoading(false);
        }
    }, [selectedUserId, period, providerFilter]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const selectUser = (userId) => {
        const next = new URLSearchParams(searchParams);
        next.set('user', userId);
        setSearchParams(next);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadLeaderboard();
    };

    const totalTokens = analytics
        ? analytics.total_input_tokens + analytics.total_output_tokens
        : 0;
    const maxPeriodTokens = analytics?.by_period?.length
        ? Math.max(...analytics.by_period.map((p) => p.input_tokens + p.output_tokens), 1)
        : 1;
    const recentLlmRecords = (analytics?.recent_records || []).filter(
        (r) => r.agent_name !== 'publish'
    );

    return (
        <div className="admin-users">
            <div className="admin-header">
                <h1>User Spend</h1>
                <p>Settled Threedium credits and usage per user. Superadmin only.</p>
            </div>

            <div className="admin-filters">
                <form onSubmit={handleSearch} className="search-form">
                    <input
                        type="text"
                        placeholder="Search by email or name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <button type="submit">Search</button>
                </form>
                <select
                    value={period}
                    onChange={(e) => {
                        setPeriod(e.target.value);
                        setPage(1);
                    }}
                >
                    {PERIOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            <div className="admin-table-container">
                {listLoading ? (
                    <div className="table-loading">
                        <div className="spinner"></div>
                    </div>
                ) : listError ? (
                    <div className="admin-error">
                        <p>{formatErrorMessage(listError)}</p>
                        <button type="button" onClick={loadLeaderboard}>Retry</button>
                    </div>
                ) : leaderboard.length === 0 ? (
                    <div className="admin-empty">No usage in this period.</div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Tier</th>
                                <th>Credits spent</th>
                                <th>Est. USD</th>
                                <th>Runs</th>
                                <th>Tokens</th>
                                <th>Requests</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboard.map((row) => (
                                <tr
                                    key={row.user_id}
                                    className={`admin-usage-row${selectedUserId === row.user_id ? ' selected' : ''}`}
                                    onClick={() => selectUser(row.user_id)}
                                >
                                    <td>
                                        <div className="user-info">
                                            <span className="user-name">{row.display_name || 'No name'}</span>
                                            <span className="user-email">{row.email}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`tier-badge ${row.tier}`}>{row.tier}</span>
                                    </td>
                                    <td>{formatCredits(row.credits_spent)}</td>
                                    <td>{formatUsd(row.estimated_cost_usd)}</td>
                                    <td>{row.spend_runs}</td>
                                    <td>{formatNumber((row.input_tokens || 0) + (row.output_tokens || 0))}</td>
                                    <td>{row.requests}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="admin-pagination">
                <span>Showing {leaderboard.length} of {total} users</span>
                <div className="pagination-controls">
                    <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span>Page {page}</span>
                    <button disabled={leaderboard.length < 20} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>

            {selectedUserId && (
                <div className="admin-usage-detail">
                    {detailLoading && !analytics ? (
                        <div className="usage-loading">
                            <div className="spinner" />
                            <p>Loading user analytics...</p>
                        </div>
                    ) : detailError ? (
                        <div className="usage-error">
                            <p>{formatErrorMessage(detailError)}</p>
                            <button type="button" onClick={loadDetail}>Retry</button>
                        </div>
                    ) : analytics ? (
                        <div className="usage-page admin-usage-embedded">
                            <div className="usage-header">
                                <div>
                                    <h1>{analytics.display_name || analytics.email}</h1>
                                    <p className="admin-usage-email">{analytics.email}</p>
                                </div>
                                <div className="usage-filters">
                                    <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                                        <option value="">All Providers</option>
                                        <option value="bedrock">AWS Bedrock</option>
                                        <option value="gemini-vertex">Google Vertex AI</option>
                                        <option value="anthropic">Anthropic</option>
                                        <option value="openai">OpenAI</option>
                                    </select>
                                </div>
                            </div>

                            <div className="usage-cards">
                                <div className="usage-card">
                                    <div className="card-label">Credits spent</div>
                                    <div className="card-value">{formatCredits(analytics.credits_spent)}</div>
                                    <div className="card-detail">{analytics.spend_runs} settled runs · {formatUsd(analytics.estimated_cost_usd)}</div>
                                </div>
                                <div className="usage-card">
                                    <div className="card-label">Estimated credits</div>
                                    <div className="card-value">{formatCredits(analytics.total_credits)}</div>
                                    <div className="card-detail">{analytics.total_requests} requests</div>
                                </div>
                                <div className="usage-card">
                                    <div className="card-label">Total Tokens</div>
                                    <div className="card-value">{formatNumber(totalTokens)}</div>
                                    <div className="card-detail">
                                        {formatNumber(analytics.total_input_tokens)} in / {formatNumber(analytics.total_output_tokens)} out
                                    </div>
                                </div>
                                <div className="usage-card">
                                    <div className="card-label">Models</div>
                                    <div className="card-value">{analytics.by_model.length}</div>
                                    <div className="card-detail">tier {analytics.tier || '—'}</div>
                                </div>
                            </div>

                            {analytics.by_period.length > 0 && (
                                <div className="usage-section">
                                    <h2>Usage Over Time</h2>
                                    <div className="usage-chart">
                                        {analytics.by_period.map((p, i) => {
                                            const tokens = p.input_tokens + p.output_tokens;
                                            const height = (tokens / maxPeriodTokens) * 100;
                                            const label = period === 'daily'
                                                ? new Date(p.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                                : new Date(p.period).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                                            return (
                                                <div key={i} className="chart-bar-wrapper" title={`${formatNumber(tokens)} tokens\n${formatCredits(p.credits)}`}>
                                                    <div className="chart-bar" style={{ height: `${Math.max(2, height)}%` }}>
                                                        <div className="chart-bar-input" style={{ height: `${tokens > 0 ? (p.input_tokens / tokens) * 100 : 50}%` }} />
                                                        <div className="chart-bar-output" />
                                                    </div>
                                                    <div className="chart-label">{label}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="chart-legend">
                                        <span><span className="legend-dot input" />Input</span>
                                        <span><span className="legend-dot output" />Output</span>
                                    </div>
                                </div>
                            )}

                            {analytics.by_model.length > 0 && (
                                <div className="usage-section">
                                    <h2>By Model</h2>
                                    <div className="usage-table-wrapper">
                                        <table className="usage-table">
                                            <thead>
                                                <tr>
                                                    <th>Model</th>
                                                    <th>Provider</th>
                                                    <th>Input Tokens</th>
                                                    <th>Output Tokens</th>
                                                    <th>Requests</th>
                                                    <th>Credits</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analytics.by_model.map((m, i) => (
                                                    <tr key={i}>
                                                        <td className="model-name">{formatModelName(m.model_id)}</td>
                                                        <td>
                                                            <span className={`provider-badge ${m.provider}`}>
                                                                {PROVIDER_LABELS[m.provider] || m.provider}
                                                            </span>
                                                        </td>
                                                        <td>{formatNumber(m.input_tokens)}</td>
                                                        <td>{formatNumber(m.output_tokens)}</td>
                                                        <td>{m.requests}</td>
                                                        <td className="cost">{formatCredits(m.credits)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {recentLlmRecords.length > 0 && (
                                <div className="usage-section">
                                    <h2>Recent Activity</h2>
                                    <div className="usage-table-wrapper">
                                        <table className="usage-table recent">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Agent</th>
                                                    <th>Model</th>
                                                    <th>Tokens</th>
                                                    <th>Credits</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentLlmRecords.map((r, i) => (
                                                    <tr key={i}>
                                                        <td className="time">{new Date(r.recorded_at).toLocaleString()}</td>
                                                        <td>{r.agent_name || '—'}</td>
                                                        <td className="model-name">{formatModelName(r.model_id)}</td>
                                                        <td>{formatNumber(r.input_tokens + r.output_tokens)}</td>
                                                        <td className="cost">{r.credits != null ? formatCredits(r.credits) : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {analytics.total_requests === 0 && analytics.spend_runs === 0 && (
                                <div className="usage-empty">
                                    <p>No usage or settled spend for this user in the selected period.</p>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
}

export default AdminUsage;
